import { z } from "zod";
import { Id, IsoDate, Page, PageQuery } from "./common.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Plans & entitlements (ALP-81): ONE system for features, limits, usage and enforcement.
 *
 * - The catalog (features + limits) is code (below). Plans are DATA (`plans` table, seeded from DEFAULT_PLANS, editable
 *   and versioned in the admin console). An org has a plan + overrides (any feature or limit, optional expiry, required
 *   reason) and a trial end date.
 * - Effective entitlements = plan ⊕ active overrides, computed by `resolveEntitlements` (pure; shared by API, worker and
 *   the web mock). The API caches the result in Valkey and invalidates it on every change.
 * - Usage is metered per org + metric + period (`usage_counters`), with a nightly reconcile from the source tables.
 * - Enforcement returns RFC 9457 `feature_not_in_plan` (403) / `limit_reached` (429) with `details` = `EntitlementDenial`.
 * - The per-org AI budget (ALP-80) is the `ai_budget_usd` limit: the org's LiteLLM virtual key `max_budget` follows it.
 */

// ---------------------------------------------------------------- catalog: features
export const FEATURE_KEYS = [
  "copilot", "voice", "custom_dashboards", "calendar_intelligence", "crisis_alerts", "automation_rules", "ai_replies",
  "semantic_search", "commenter_enrichment", "bulk_actions", "exports", "approvals", "customer_360", "api_access",
  "channel_instagram", "channel_facebook", "channel_messenger", "channel_whatsapp", "byok",
] as const;
export const FeatureKey = z.enum(FEATURE_KEYS);
export type FeatureKeyT = (typeof FEATURE_KEYS)[number];
export const FeatureGroup = z.enum(["ai", "engagement", "insights", "platform", "channels"]);
export interface FeatureDef {
  key: FeatureKeyT;
  type: "boolean";
  group: z.infer<typeof FeatureGroup>;
  /** Channels that are not built yet stay off in every plan and show "coming soon". */
  available: boolean;
}
export const FEATURES: readonly FeatureDef[] = [
  { key: "copilot", type: "boolean", group: "ai", available: true },
  { key: "voice", type: "boolean", group: "ai", available: true },
  { key: "ai_replies", type: "boolean", group: "ai", available: true },
  { key: "semantic_search", type: "boolean", group: "ai", available: true },
  /** ALP-86: client-owned provider keys + per-org model routing. Off on every plan; switched on per org (admin override). */
  { key: "byok", type: "boolean", group: "ai", available: true },
  { key: "automation_rules", type: "boolean", group: "engagement", available: true },
  { key: "approvals", type: "boolean", group: "engagement", available: true },
  { key: "bulk_actions", type: "boolean", group: "engagement", available: true },
  { key: "commenter_enrichment", type: "boolean", group: "engagement", available: true },
  { key: "customer_360", type: "boolean", group: "engagement", available: true },
  { key: "custom_dashboards", type: "boolean", group: "insights", available: true },
  { key: "calendar_intelligence", type: "boolean", group: "insights", available: true },
  { key: "crisis_alerts", type: "boolean", group: "insights", available: true },
  { key: "exports", type: "boolean", group: "platform", available: true },
  { key: "api_access", type: "boolean", group: "platform", available: true },
  { key: "channel_instagram", type: "boolean", group: "channels", available: true },
  { key: "channel_facebook", type: "boolean", group: "channels", available: false },
  { key: "channel_messenger", type: "boolean", group: "channels", available: false },
  { key: "channel_whatsapp", type: "boolean", group: "channels", available: false },
];

// ---------------------------------------------------------------- catalog: limits
export const LIMIT_KEYS = [
  "channel_accounts", "seats", "ai_budget_usd", "ai_tokens", "ai_rpm", "ai_tpm", "ai_images", "copilot_messages", "active_rules", "automation_runs",
  "scheduled_posts", "custom_dashboards", "backfill_posts", "backfill_days", "storage_mb", "exports", "api_rpm", "agent_replies",
] as const;
export const LimitKey = z.enum(LIMIT_KEYS);
export type LimitKeyT = (typeof LIMIT_KEYS)[number];
export const LimitType = z.enum(["count", "money", "rate"]);
export const LimitPeriod = z.enum(["none", "day", "month"]);
export type LimitPeriodT = z.infer<typeof LimitPeriod>;
export interface LimitDef {
  key: LimitKeyT;
  type: z.infer<typeof LimitType>;
  period: LimitPeriodT;
  /** Display unit: accounts, seats, USD, tokens, rpm, tpm, messages, rules, runs, posts, dashboards, days, MB, exports. */
  unit: string;
  /**
   * gauge = current amount, counted from the source table (accounts connected, seats, active rules, dashboards, storage);
   * counter = consumed per period (atomic increments, reconciled nightly); setting = a cap applied as configuration
   * (backfill depth, rate limits): never "used up".
   */
  kind: "gauge" | "counter" | "setting";
  /** Feature this limit belongs to (the UI groups them; a disabled feature hides its limits). */
  feature?: FeatureKeyT;
}
export const LIMITS: readonly LimitDef[] = [
  { key: "channel_accounts", type: "count", period: "none", unit: "accounts", kind: "gauge" },
  { key: "seats", type: "count", period: "none", unit: "seats", kind: "gauge" },
  { key: "ai_budget_usd", type: "money", period: "month", unit: "USD", kind: "counter" },
  { key: "ai_tokens", type: "count", period: "month", unit: "tokens", kind: "counter" },
  /** AI image generations + AI image edits per month. */
  { key: "ai_images", type: "count", period: "month", unit: "images", kind: "counter", feature: "ai_replies" },
  { key: "ai_rpm", type: "rate", period: "none", unit: "rpm", kind: "setting" },
  { key: "ai_tpm", type: "rate", period: "none", unit: "tpm", kind: "setting" },
  { key: "copilot_messages", type: "count", period: "day", unit: "messages", kind: "counter", feature: "copilot" },
  { key: "active_rules", type: "count", period: "none", unit: "rules", kind: "gauge", feature: "automation_rules" },
  { key: "automation_runs", type: "count", period: "month", unit: "runs", kind: "counter", feature: "automation_rules" },
  { key: "scheduled_posts", type: "count", period: "month", unit: "posts", kind: "counter" },
  { key: "custom_dashboards", type: "count", period: "none", unit: "dashboards", kind: "gauge", feature: "custom_dashboards" },
  { key: "backfill_posts", type: "count", period: "none", unit: "posts", kind: "setting" },
  { key: "backfill_days", type: "count", period: "none", unit: "days", kind: "setting" },
  { key: "storage_mb", type: "count", period: "none", unit: "MB", kind: "gauge" },
  { key: "exports", type: "count", period: "month", unit: "exports", kind: "counter", feature: "exports" },
  { key: "api_rpm", type: "rate", period: "none", unit: "rpm", kind: "setting", feature: "api_access" },
  /** ALP-83: AI-agent replies (drafts + sends) per month; missing in older plan rows = unlimited. */
  { key: "agent_replies", type: "count", period: "month", unit: "replies", kind: "counter", feature: "ai_replies" },
];
export const limitDef = (k: LimitKeyT): LimitDef => LIMITS.find((l) => l.key === k)!;

// ---------------------------------------------------------------- plans
export const PLAN_KEYS = ["trial", "free", "pro", "business"] as const;
export const PlanKey = z.enum(PLAN_KEYS);
export type PlanKeyT = (typeof PLAN_KEYS)[number];
/** Limit value: a number, or null = unlimited. */
export const LimitValue = z.number().min(0).nullable();
export const PlanFeatures = z.record(FeatureKey, z.boolean());
export const PlanLimits = z.record(LimitKey, LimitValue);
export const PlanDefinition = z.object({
  key: PlanKey,
  name: z.string().min(1).max(60),
  description: z.string().max(500),
  features: PlanFeatures,
  limits: PlanLimits,
  /** Trial only: length of a new trial in days (default 14); when it ends the org falls back to `free`. */
  trialDays: z.number().int().min(1).max(365).nullable(),
  /** Incremented on every edit (changing a plan applies to every org on it, unless overridden). */
  version: z.number().int(),
  updatedAt: IsoDate.nullable(),
  updatedBy: z.string().nullable(),
  /** Orgs currently on this plan (admin view). */
  orgCount: z.number().int().optional(),
});
export type PlanDefinitionT = z.infer<typeof PlanDefinition>;
export const TRIAL_DAYS_DEFAULT = 14;
export const TRIAL_FALLBACK_PLAN: PlanKeyT = "free";

const all = <T>(v: T) => Object.fromEntries(FEATURE_KEYS.map((k) => [k, v])) as Record<FeatureKeyT, T>;
const futureChannelsOff = { channel_facebook: false, channel_messenger: false, channel_whatsapp: false } as const;
/** Off on every plan: BYOK is enabled per org with an admin override (ALP-86). */
const byokOff = { byok: false } as const;
/** Seed data for the `plans` table (re-runnable: the seed inserts missing plans, never overwrites admin edits). */
export const DEFAULT_PLANS: readonly Omit<PlanDefinitionT, "version" | "updatedAt" | "updatedBy" | "orgCount">[] = [
  {
    key: "trial", name: "Trial", description: "Everything in Pro for 14 days, then Free.", trialDays: TRIAL_DAYS_DEFAULT,
    features: { ...all(true), ...futureChannelsOff, ...byokOff, api_access: false },
    limits: {
      channel_accounts: 2, seats: 3, ai_budget_usd: 5, ai_tokens: 2_000_000, ai_images: 30, ai_rpm: 30, ai_tpm: 60_000, copilot_messages: 50, active_rules: 10,
      automation_runs: 2_000, scheduled_posts: 60, custom_dashboards: 5, backfill_posts: 200, backfill_days: 90, storage_mb: 1_000, exports: 5, api_rpm: 0, agent_replies: 500,
    },
  },
  {
    key: "free", name: "Free", description: "One account, the essentials.", trialDays: null,
    features: {
      ...all(false), channel_instagram: true, automation_rules: true, approvals: true, customer_360: true, calendar_intelligence: true, ai_replies: true,
    },
    limits: {
      channel_accounts: 1, seats: 1, ai_budget_usd: 1, ai_tokens: 200_000, ai_images: 5, ai_rpm: 10, ai_tpm: 20_000, copilot_messages: 0, active_rules: 3,
      automation_runs: 300, scheduled_posts: 10, custom_dashboards: 0, backfill_posts: 50, backfill_days: 30, storage_mb: 200, exports: 0, api_rpm: 0, agent_replies: 50,
    },
  },
  {
    key: "pro", name: "Pro", description: "For growing brands: Copilot, dashboards, bulk moderation.", trialDays: null,
    features: { ...all(true), ...futureChannelsOff, ...byokOff, api_access: false },
    limits: {
      channel_accounts: 3, seats: 5, ai_budget_usd: 25, ai_tokens: 10_000_000, ai_images: 200, ai_rpm: 60, ai_tpm: 200_000, copilot_messages: 200, active_rules: 50,
      automation_runs: 20_000, scheduled_posts: 300, custom_dashboards: 20, backfill_posts: 1_000, backfill_days: 365, storage_mb: 10_000, exports: 50, api_rpm: 0, agent_replies: 5_000,
    },
  },
  {
    key: "business", name: "Business", description: "Teams and agencies: everything, generous limits, API access.", trialDays: null,
    features: { ...all(true), ...futureChannelsOff, ...byokOff },
    limits: {
      channel_accounts: 15, seats: 25, ai_budget_usd: 150, ai_tokens: null, ai_images: 2_000, ai_rpm: 300, ai_tpm: 1_000_000, copilot_messages: 2_000, active_rules: null,
      automation_runs: null, scheduled_posts: null, custom_dashboards: null, backfill_posts: null, backfill_days: null, storage_mb: 100_000, exports: null, api_rpm: 600, agent_replies: null,
    },
  },
];

// ---------------------------------------------------------------- per-org state
export const EntitlementStatus = z.enum(["trial", "active", "past_due"]);
export const OverrideKind = z.enum(["feature", "limit"]);
export const EntitlementOverride = z.object({
  id: Id,
  kind: OverrideKind,
  key: z.string(), // FeatureKey or LimitKey (validated by kind)
  /** feature: boolean; limit: number or null (= unlimited). */
  value: z.union([z.boolean(), z.number(), z.null()]),
  reason: z.string(),
  expiresAt: IsoDate.nullable(),
  createdBy: z.string().nullable(),
  createdAt: IsoDate,
  revokedAt: IsoDate.nullable(),
  /** active = applies now; expired/revoked are kept for history. */
  state: z.enum(["active", "expired", "revoked"]),
});
export type EntitlementOverrideT = z.infer<typeof EntitlementOverride>;
export const OrgEntitlementState = z.object({
  planKey: PlanKey,
  status: EntitlementStatus,
  trialEndsAt: IsoDate.nullable(),
  planAssignedAt: IsoDate.nullable(),
});
export type OrgEntitlementStateT = z.infer<typeof OrgEntitlementState>;

// ---------------------------------------------------------------- effective entitlements (the app reads this)
export const EntitlementSource = z.enum(["plan", "override"]);
export const EffectiveFeature = z.object({
  key: FeatureKey,
  enabled: z.boolean(),
  source: EntitlementSource,
  overrideId: Id.nullable(),
  expiresAt: IsoDate.nullable(),
  available: z.boolean(),
});
export const EffectiveLimit = z.object({
  key: LimitKey,
  type: LimitType,
  period: LimitPeriod,
  unit: z.string(),
  kind: z.enum(["gauge", "counter", "setting"]),
  /** null = unlimited. */
  limit: LimitValue,
  /** Current usage (gauge: now; counter: this period); null for settings. */
  used: z.number().nullable(),
  remaining: z.number().nullable(),
  /** used / limit (0..∞), null when unlimited or a setting. */
  ratio: z.number().nullable(),
  /** Start of the next period (UTC) for day/month limits. */
  resetAt: IsoDate.nullable(),
  source: EntitlementSource,
  overrideId: Id.nullable(),
  expiresAt: IsoDate.nullable(),
});
export const EntitlementWarning = z.object({ key: LimitKey, level: z.enum(["near", "reached"]), ratio: z.number() });
export const EffectiveEntitlements = z.object({
  orgId: Id,
  plan: z.object({ key: PlanKey, name: z.string(), version: z.number().int() }),
  /** The plan the org is assigned (differs from `plan` when a trial has ended: assigned trial → effective free). */
  assignedPlan: PlanKey,
  status: EntitlementStatus,
  trialEndsAt: IsoDate.nullable(),
  trialExpired: z.boolean(),
  features: z.array(EffectiveFeature),
  limits: z.array(EffectiveLimit),
  /** ≥ 80 % = near, ≥ 100 % = reached (counter + gauge limits only). */
  warnings: z.array(EntitlementWarning),
  computedAt: IsoDate,
});
export type EffectiveEntitlementsT = z.infer<typeof EffectiveEntitlements>;
export type EffectiveLimitT = z.infer<typeof EffectiveLimit>;
export type EffectiveFeatureT = z.infer<typeof EffectiveFeature>;

/** `details` of a `feature_not_in_plan` (403) / `limit_reached` (429) problem. */
export const EntitlementDenial = z.object({
  reason: z.enum(["feature_not_in_plan", "limit_reached"]),
  feature: FeatureKey.optional(),
  limit: LimitKey.optional(),
  max: LimitValue.optional(),
  used: z.number().optional(),
  period: LimitPeriod.optional(),
  resetAt: IsoDate.nullable().optional(),
  plan: PlanKey,
});
export type EntitlementDenialT = z.infer<typeof EntitlementDenial>;

export const NEAR_LIMIT_RATIO = 0.8;

// ---------------------------------------------------------------- pure resolution (API, worker, web mock)
/** Start of the current period (UTC). `none` → null. */
export function periodStart(period: LimitPeriodT, now: Date): Date | null {
  if (period === "none") return null;
  if (period === "day") return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}
/** Start of the next period (UTC) = when a day/month limit resets. */
export function periodReset(period: LimitPeriodT, now: Date): Date | null {
  if (period === "none") return null;
  if (period === "day") return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}
/** Counter key of the current period: `2026-10` (month), `2026-10-23` (day), `all` (none). */
export function periodKey(period: LimitPeriodT, now: Date): string {
  const s = periodStart(period, now);
  if (!s) return "all";
  return period === "day" ? s.toISOString().slice(0, 10) : s.toISOString().slice(0, 7);
}

export const overrideState = (o: { expiresAt: string | Date | null; revokedAt: string | Date | null }, now: Date): "active" | "expired" | "revoked" =>
  o.revokedAt ? "revoked" : o.expiresAt && new Date(o.expiresAt).getTime() <= now.getTime() ? "expired" : "active";

export interface ResolveInput {
  orgId: string;
  state: OrgEntitlementStateT;
  /** Every plan (the effective plan may differ from the assigned one after a trial ends). */
  plans: Pick<PlanDefinitionT, "key" | "name" | "version" | "features" | "limits">[];
  overrides: Pick<EntitlementOverrideT, "id" | "kind" | "key" | "value" | "expiresAt" | "revokedAt">[];
  /** Current usage per limit key (gauges: now; counters: this period). Missing = 0 for gauge/counter. */
  usage: Partial<Record<LimitKeyT, number>>;
  now: Date;
}

/** Effective entitlements = plan ⊕ active overrides (latest override per key wins); trial past its end → free. */
export function resolveEntitlements(i: ResolveInput): EffectiveEntitlementsT {
  const trialExpired = i.state.planKey === "trial" && !!i.state.trialEndsAt && new Date(i.state.trialEndsAt).getTime() <= i.now.getTime();
  const effectiveKey: PlanKeyT = trialExpired ? TRIAL_FALLBACK_PLAN : i.state.planKey;
  const plan = i.plans.find((p) => p.key === effectiveKey) ?? i.plans.find((p) => p.key === TRIAL_FALLBACK_PLAN);
  if (!plan) throw new Error(`plan ${effectiveKey} not found`);
  const active = i.overrides.filter((o) => overrideState(o, i.now) === "active");
  // later overrides win: callers pass them oldest first
  const pick = (kind: "feature" | "limit", key: string) => active.filter((o) => o.kind === kind && o.key === key).at(-1) ?? null;

  const features = FEATURES.map((f) => {
    const o = pick("feature", f.key);
    const enabled = f.available && (o ? o.value === true : plan.features[f.key] === true);
    return { key: f.key, enabled, source: o ? ("override" as const) : ("plan" as const), overrideId: o?.id ?? null, expiresAt: o?.expiresAt ? new Date(o.expiresAt).toISOString() : null, available: f.available };
  });
  const limits = LIMITS.map((l) => {
    const o = pick("limit", l.key);
    const raw = o ? (typeof o.value === "number" ? o.value : null) : (plan.limits[l.key] ?? null);
    const used = l.kind === "setting" ? null : (i.usage[l.key] ?? 0);
    const reset = periodReset(l.period, i.now);
    return {
      key: l.key, type: l.type, period: l.period, unit: l.unit, kind: l.kind,
      limit: raw, used,
      remaining: raw === null || used === null ? null : Math.max(0, raw - used),
      ratio: raw === null || used === null ? null : raw === 0 ? (used > 0 ? Infinity : 1) : used / raw,
      resetAt: reset ? reset.toISOString() : null,
      source: o ? ("override" as const) : ("plan" as const), overrideId: o?.id ?? null, expiresAt: o?.expiresAt ? new Date(o.expiresAt).toISOString() : null,
    };
  });
  const warnings = limits
    .filter((l) => l.kind !== "setting" && l.ratio !== null && l.limit !== 0 && l.ratio >= NEAR_LIMIT_RATIO)
    .map((l) => ({ key: l.key, level: l.ratio! >= 1 ? ("reached" as const) : ("near" as const), ratio: Number.isFinite(l.ratio!) ? l.ratio! : 1 }));
  return {
    orgId: i.orgId,
    plan: { key: plan.key, name: plan.name, version: plan.version },
    assignedPlan: i.state.planKey,
    status: i.state.status,
    trialEndsAt: i.state.trialEndsAt ? new Date(i.state.trialEndsAt).toISOString() : null,
    trialExpired,
    features,
    limits: limits.map((l) => ({ ...l, ratio: l.ratio !== null && !Number.isFinite(l.ratio) ? 1e9 : l.ratio })),
    warnings,
    computedAt: i.now.toISOString(),
  };
}

export const featureEnabled = (e: EffectiveEntitlementsT, k: FeatureKeyT) => e.features.find((f) => f.key === k)?.enabled === true;
export const limitOf = (e: EffectiveEntitlementsT, k: LimitKeyT) => e.limits.find((l) => l.key === k)!;
/** Would consuming `amount` more of `k` stay within the limit? (unlimited/settings → true). */
export function withinLimit(e: EffectiveEntitlementsT, k: LimitKeyT, amount = 1): boolean {
  const l = limitOf(e, k);
  if (l.limit === null || l.used === null) return true;
  return l.used + amount <= l.limit;
}

// ---------------------------------------------------------------- admin console
export const UsagePoint = z.object({ period: z.string(), used: z.number() });
export const UsageHistory = z.object({ key: LimitKey, period: LimitPeriod, points: z.array(UsagePoint) });
/** ALP-80: the org's LiteLLM virtual key (budget = the `ai_budget_usd` limit). */
export const OrgAiKeyStatus = z.object({
  /** virtual_key = per-org LiteLLM key; global_key = the gateway has no DB (local host-run): spend attribution only. */
  mode: z.enum(["virtual_key", "global_key"]),
  keyAlias: z.string().nullable(),
  keyHint: z.string().nullable(),
  maxBudget: z.number().nullable(),
  rpmLimit: z.number().nullable(),
  tpmLimit: z.number().nullable(),
  spendThisMonth: z.number(),
  /** litellm = the key's spend from /key/info (+ Dify-reported spend); recorded = Malfak's own usage counters. */
  spendSource: z.enum(["litellm", "recorded"]),
  budgetResetAt: IsoDate.nullable(),
  exceeded: z.boolean(),
  lastError: z.string().nullable(),
  rotatedAt: IsoDate.nullable(),
});
export type OrgAiKeyStatusT = z.infer<typeof OrgAiKeyStatus>;
export const AdminOrgEntitlements = z.object({
  orgId: Id,
  orgName: z.string(),
  state: OrgEntitlementState,
  effective: EffectiveEntitlements,
  overrides: z.array(EntitlementOverride),
  history: z.array(UsageHistory),
  aiKey: OrgAiKeyStatus.nullable(),
});
export const EntitlementChange = z.object({
  id: Id,
  at: IsoDate,
  actor: z.object({ id: z.string().nullable(), email: z.string().nullable(), type: z.enum(["user", "system"]) }),
  orgId: Id.nullable(),
  orgName: z.string().nullable(),
  planKey: PlanKey.nullable(),
  action: z.enum(["plan.updated", "org.plan_set", "org.status_set", "org.trial_extended", "org.trial_ended", "override.created", "override.revoked", "plan.bulk_applied", "ai_key.rotated", "ai_key.provisioned", "backfill"]),
  before: z.unknown(),
  after: z.unknown(),
  reason: z.string().nullable(),
});
export const Reason = z.string().trim().min(3).max(500);
export const UpdatePlanRequest = z.object({
  name: z.string().min(1).max(60).optional(),
  description: z.string().max(500).optional(),
  features: z.partialRecord(FeatureKey, z.boolean()).optional(),
  limits: z.partialRecord(LimitKey, LimitValue).optional(),
  trialDays: z.number().int().min(1).max(365).nullable().optional(),
  reason: Reason,
});
export const SetOrgEntitlementRequest = z.object({
  planKey: PlanKey.optional(),
  status: EntitlementStatus.optional(),
  trialEndsAt: IsoDate.nullable().optional(),
  reason: Reason,
});
export const TrialActionRequest = z.object({ action: z.enum(["extend", "end"]), days: z.number().int().min(1).max(365).optional(), reason: Reason });
export const CreateOverrideRequest = z.object({
  kind: OverrideKind,
  key: z.string().min(1).max(40),
  value: z.union([z.boolean(), z.number().min(0), z.null()]),
  expiresAt: IsoDate.nullable().optional(),
  reason: Reason,
});
export const RevokeOverrideRequest = z.object({ reason: Reason });
export const BulkApplyPlanRequest = z.object({ orgIds: z.array(Id).min(1).max(500), reason: Reason });
export const BulkApplyPlanResult = z.object({ updated: z.number(), results: z.array(z.object({ orgId: Id, ok: z.boolean(), error: z.string().nullable() })) });
export const EntitlementAuditQuery = PageQuery.extend({ orgId: Id.optional(), planKey: PlanKey.optional() });
export const RotateAiKeyRequest = z.object({ reason: Reason });
/** Orgs list row extras (admin): plan + the headline usage bars. */
export const OrgPlanSummary = z.object({
  planKey: PlanKey,
  effectivePlan: PlanKey,
  status: EntitlementStatus,
  trialEndsAt: IsoDate.nullable(),
  usage: z.array(z.object({ key: LimitKey, used: z.number().nullable(), limit: LimitValue })),
});

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const ById = z.object({ id: z.string().uuid() });
const PlanP = z.object({ key: PlanKey });

export const entitlementRoutes = {
  entitlements: route({ method: "GET", path: "/v1/orgs/:orgId/entitlements", params: OrgP, response: EffectiveEntitlements, summary: "Effective plan features, limits, usage and near-limit warnings of the org" }),
  // ---- admin console (platform_admin, audited; POSTs need an Idempotency-Key)
  adminPlans: route({ method: "GET", path: "/v1/admin/plans", response: z.array(PlanDefinition), summary: "Plans with their features, limits, version and org count" }),
  adminUpdatePlan: route({ method: "PUT", path: "/v1/admin/plans/:key", params: PlanP, body: UpdatePlanRequest, response: PlanDefinition, summary: "Edit a plan (new version; applies to every org on it unless overridden)" }),
  adminBulkApplyPlan: route({ method: "POST", path: "/v1/admin/plans/:key/apply", params: PlanP, body: BulkApplyPlanRequest, response: BulkApplyPlanResult, summary: "Move the selected orgs to this plan" }),
  adminOrgEntitlements: route({ method: "GET", path: "/v1/admin/orgs/:id/entitlements", params: ById, response: AdminOrgEntitlements, summary: "Org plan, overrides, effective entitlements, usage history, AI key" }),
  adminSetOrgEntitlement: route({ method: "PUT", path: "/v1/admin/orgs/:id/entitlements", params: ById, body: SetOrgEntitlementRequest, response: AdminOrgEntitlements, summary: "Set the org's plan / status / trial end (reason required)" }),
  adminOrgTrial: route({ method: "POST", path: "/v1/admin/orgs/:id/trial", params: ById, body: TrialActionRequest, response: AdminOrgEntitlements, summary: "Extend or end the org's trial" }),
  adminCreateOverride: route({ method: "POST", path: "/v1/admin/orgs/:id/overrides", params: ById, body: CreateOverrideRequest, response: AdminOrgEntitlements, status: 201, summary: "Override one feature or limit for the org (optional expiry, reason required)" }),
  adminRevokeOverride: route({ method: "POST", path: "/v1/admin/orgs/:id/overrides/:overrideId/revoke", params: ById.extend({ overrideId: z.string().uuid() }), body: RevokeOverrideRequest, response: AdminOrgEntitlements, summary: "Revoke an override (kept in history)" }),
  adminRotateAiKey: route({ method: "POST", path: "/v1/admin/orgs/:id/ai-key/rotate", params: ById, body: RotateAiKeyRequest, response: OrgAiKeyStatus, summary: "Rotate the org's LiteLLM virtual key (old key revoked)" }),
  adminEntitlementAudit: route({ method: "GET", path: "/v1/admin/entitlements/audit", query: EntitlementAuditQuery, response: Page(EntitlementChange), summary: "Every plan / entitlement change (who, when, before → after, reason)" }),
} as const;

export type PlanKeyType = PlanKeyT;
export type EntitlementChangeT = z.infer<typeof EntitlementChange>;
export type AdminOrgEntitlementsT = z.infer<typeof AdminOrgEntitlements>;
